import { useSession } from "@/lib/auth/auth";
import { useQuery } from "@apollo/client";

import React, {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
} from "react";

import {
  ACCOUNTS_QUERY,
  TAGS_QUERY,
  TAX_CODES_QUERY,
} from "@/apis/gql/apolloClient";
import type {
  AccountDetails,
  ExportHistoryType,
  TaxCode,
} from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { useGetAllExports } from "@/queries/exports/get-all.legacy";
import type { UseGetAllInvoicesData } from "@/queries/invoices/get-all";

export enum NewRowIds {
  NewLineItemId = "newLineItemId",
}

interface InvoiceProviderProps {
  children: React.ReactNode;
  invoices: UseGetAllInvoicesData;
  loading: boolean;
}
interface InvoicesContextProps {
  loading: boolean;
  invoices: UseGetAllInvoicesData;
  accounts: AccountDetails[] | undefined;
  taxCodes: TaxCode[] | undefined;
  exportHistory: any;
  refetchExportHistory: () => void;
  tags: any;
}

const InvoicesContext = createContext<InvoicesContextProps | undefined>(
  undefined,
);

const _InvoiceProvider: React.FC<InvoiceProviderProps> = ({
  children,
  invoices,
  loading,
}) => {
  const { tenantId } = useTenant();

  const { data: accountsData, loading: accountsLoading } = useQuery(
    ACCOUNTS_QUERY,
    {
      variables: { tenantId },
    },
  );

  const { data: taxCodesData, loading: taxCodesLoading } = useQuery(
    TAX_CODES_QUERY,
    {
      skip: !tenantId || tenantId === "",
      variables: { tenantId },
    },
  );

  const { exports: exportHistoryData, refetch: refetchExportHistory } =
    useGetAllExports();

  const session = useSession();

  const { data: tagsData, loading: tagsLoading } = useQuery(TAGS_QUERY, {
    skip: !tenantId || tenantId === "" || !session,
  });

  const [accounts, setAccounts] = React.useState<AccountDetails[] | undefined>(
    [],
  );
  const [exportHistory, setExportHistory] = React.useState<
    ExportHistoryType[] | undefined
  >([]);
  const [taxCodes, setTaxCodes] = React.useState<TaxCode[] | undefined>([]);
  const [tags, setTags] = React.useState<any[] | undefined>([]);

  useEffect(() => {
    if (exportHistoryData) setExportHistory(exportHistoryData);
  }, [exportHistoryData]);

  // Initialize Accounts State
  useEffect(() => {
    if (accountsData) {
      setAccounts(accountsData.accounts);
    }
  }, [accountsData]);

  // Initialize Tax State
  useEffect(() => {
    if (taxCodesData) {
      setTaxCodes(taxCodesData.taxCodes);
    }
  }, [taxCodesData]);

  // Initialize Tags State
  useEffect(() => {
    if (tagsData) {
      setTags(tagsData.tags);
    }
  }, [tagsData]);

  const _loading = React.useMemo(() => {
    return loading || accountsLoading || taxCodesLoading || tagsLoading;
  }, [loading, accountsLoading, taxCodesLoading, tagsLoading]);

  const context = React.useMemo(
    () => ({
      loading: _loading,
      invoices: invoices ?? [],
      accounts,
      taxCodes,
      tags,
      exportHistory,
      refetchExportHistory,
    }),
    [
      accounts,
      exportHistory,
      refetchExportHistory,
      invoices,
      _loading,
      taxCodes,
      tags,
    ],
  );

  return (
    <InvoicesContext.Provider value={context}>
      {children}
    </InvoicesContext.Provider>
  );
};

export const InvoicesProvider = ({
  children,
  invoices,
  loading,
}: PropsWithChildren<{
  invoices: UseGetAllInvoicesData;
  loading: boolean;
}>) => {
  return (
    <_InvoiceProvider invoices={invoices} loading={loading}>
      {children}
    </_InvoiceProvider>
  );
};

export const useInvoicesContext = () => {
  const context = useContext(InvoicesContext);
  if (!context)
    throw new Error("useStateContext must be used within a StateProvider");

  return {
    invoices: context.invoices,
    accounts: context.accounts,
    taxCodes: context.taxCodes,
    tags: context.tags,
    loading: context.loading,
  };
};

export const useExportHistory = () => {
  const context = useContext(InvoicesContext);
  if (!context)
    throw new Error("useStateContext must be used within a StateProvider");
  return {
    exportHistory: context.exportHistory,
    refetchExportHistory: context.refetchExportHistory,
    loading: context.loading,
  };
};
